import { useState } from 'react';
import { Check, ChevronDown, Flag } from 'lucide-react';
import type { Universe } from '../../types';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface UniverseDetailsProps {
  selectedUniverse: Universe;
  /** Hide the "Institutional-Grade Data Quality" card — the compact preview
   *  card doesn't need it; the full step-1 overview still shows it. */
  hideDataQuality?: boolean;
}

export function UniverseDetails({
  selectedUniverse,
  hideDataQuality = false,
}: UniverseDetailsProps) {
  const [showMore, setShowMore] = useState(false);

  // Descriptive index metadata (provider, coverage label, cadence, segments,
  // blurb) comes from the API — the backend owns it. `indexRecon/RebalanceFrequency`
  // is the INDEX's published cadence, deliberately distinct from the editable
  // `rebalancerDetails` schedule, so the Overview always shows the index's own
  // rhythm rather than the user's configuration.
  return (
    <Collapsible open={showMore} onOpenChange={setShowMore}>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/20">
        <div className="flex items-center gap-1.5 text-[14px] text-muted-foreground">
          <Flag className="w-3 h-3" />
          <span>Inception: {selectedUniverse.inception}</span>
        </div>
        {/* Less/More toggle: Figma renders this as muted-foreground with a
            subtle muted hover fill (not transparent), at text-xs. */}
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50"
          >
            {showMore ? 'Less' : 'More'}
            <ChevronDown
              className={cn(
                'w-3 h-3 ml-1 transition-transform',
                showMore && 'rotate-180',
              )}
            />
          </Button>
        </CollapsibleTrigger>
      </div>
      {/* Figma UniverseSelection.tsx:1893 separates the expanded content from
          the Inception row with mt-3 + a top hairline (the content wrapper is
          `mt-3 border-t border-border/20 space-y-3`). Ours had only space-y-3,
          so the Institutional-Grade card sat flush under Inception. */}
      <CollapsibleContent className="mt-3 border-t border-border/20 space-y-3">
        {/* Institutional-Grade Data Quality badge — Figma uses a gradient
            primary card (not a flat bg-primary/5), a compact icon with a
            ring, and slightly tighter typography. Verified 1:1 against
            the rendered Figma:
              card        rounded-lg border border-primary/20 p-3
                          bg-gradient-to-br from-primary/[0.03]
                          via-primary/[0.01] to-transparent
              icon ring   w-5 h-5 rounded-full bg-primary/10 ring-1 ring-primary/20
              icon        w-3 h-3 text-primary
              title       font-semibold text-primary text-[14px] tracking-tight
              body        text-[13px] text-foreground/70  */}
        {!hideDataQuality && (
        <div className="relative overflow-hidden rounded-lg border border-primary/20 bg-gradient-to-br from-primary/[0.03] via-primary/[0.01] to-transparent p-3">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
          <div className="relative flex items-start gap-2">
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center ring-1 ring-primary/20">
              <Check className="w-3 h-3 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-primary mb-1 tracking-tight text-[14px]">
                Institutional-Grade Data Quality
              </div>
              <p className="text-foreground/70 leading-relaxed text-[13px]">
                Comprehensive point-in-time constituent membership, full
                corporate action adjustments, and rigorous survivorship bias
                elimination.
              </p>
            </div>
          </div>
        </div>
        )}

        {/* Description */}
        <p className="text-[14px] text-muted-foreground leading-relaxed">
          {selectedUniverse.description}
        </p>

        {/* Universe Overview — Figma: font-medium 15px heading, 2-column
            grid (gap-x-4), each row items-center py-1 with a plain-weight
            pure-black value. */}
        <div className="space-y-2">
          {/* Static heading regardless of the selected index (per product). */}
          <h4 className="font-medium text-[15px]">Universe Overview</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="flex justify-between items-center py-1">
              <span className="text-muted-foreground text-[14px]">
                Data Provider
              </span>
              <span className="text-[14px] text-foreground">
                {selectedUniverse.dataProvider}
              </span>
            </div>
            <div className="flex justify-between items-center py-1">
              <span className="text-muted-foreground text-[14px]">Coverage</span>
              <span className="text-[14px] text-foreground">
                {selectedUniverse.coverageLabel || '—'}
              </span>
            </div>
            <div className="flex justify-between items-center py-1">
              <span className="text-muted-foreground text-[14px]">
                Asset Class
              </span>
              <span className="text-[14px] text-foreground">
                {selectedUniverse.assetClass?.label ?? '—'}
              </span>
            </div>
            <div className="flex justify-between items-center py-1">
              <span className="text-muted-foreground text-[14px]">
                Reconstitution Freq.
              </span>
              <span className="text-[14px] text-foreground">
                {selectedUniverse.indexReconFrequency || '—'}
              </span>
            </div>
            <div className="flex justify-between items-center py-1">
              <span className="text-muted-foreground text-[14px]">
                Market Segments
              </span>
              <span className="text-[13px] text-foreground">
                {selectedUniverse.marketSegments ?? '—'}
              </span>
            </div>
            <div className="flex justify-between items-center py-1">
              <span className="text-muted-foreground text-[14px]">
                Rebalance Freq.
              </span>
              <span className="text-[14px] text-foreground">
                {selectedUniverse.indexRebalanceFrequency || '—'}
              </span>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
